import { uiText, useUiLanguage } from '@/i18n/interface';
import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import Layout from '../components/Layout';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { CheckCircle, Circle, Clock, Users, Target } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { supabase } from '@/integrations/supabase/client';

interface BeltLevel {
  id: string;
  name: string;
  color: string;
  order_sequence: number;
  requirements: Array<{
    id: string;
    title: string;
    description: string;
    category: string;
  }>;
}

const BeltRequirements = () => {
  useUiLanguage();
  const { t } = useTranslation();
  const { user, profile } = useAuth();
  const [beltLevels, setBeltLevels] = useState<BeltLevel[]>([]);
  const [loading, setLoading] = useState(true);
  const [currentBeltIndex, setCurrentBeltIndex] = useState(-1);

  useEffect(() => {
    if (profile?.dojo_id) {
      fetchDojoBeltSystem();
    }
  }, [profile?.dojo_id]);

  const fetchDojoBeltSystem = async () => {
    try {
      setLoading(true);

      // Fetch the dojo's belt system with requirements from admin syllabus
      const { data: belts, error } = await supabase
        .from('belt_levels')
        .select('*')
        .eq('dojo_id', profile.dojo_id)
        .order('order_sequence');

      if (error) {
        console.error('Error fetching belt system:', error);
        return;
      }

      if (belts) {
        // Process belts to ensure requirements are properly formatted
        const processedBelts = belts.map(belt => ({
          ...belt,
          requirements: Array.isArray(belt.requirements) ? belt.requirements : []
        }));

        setBeltLevels(processedBelts);

        // Find current belt index
        if (profile?.belt_level_id) {
          const index = processedBelts.findIndex(belt => belt.id === profile.belt_level_id);
          setCurrentBeltIndex(index);
        }

        console.log('✅ Loaded', processedBelts.length, 'belt levels from admin syllabus');
      }
    } catch (error) {
      console.error('Error fetching dojo belt system:', error);
    } finally {
      setLoading(false);
    }
  };

  // Group requirements by category
  const getRequirementsByCategory = (requirements: Array<any>) => {
    if (!Array.isArray(requirements)) return {};

    const grouped: { [key: string]: Array<any> } = {};
    requirements.forEach((req) => {
      const category = req.category || 'General';
      if (!grouped[category]) {
        grouped[category] = [];
      }
      grouped[category].push(req);
    });
    return grouped;
  };

  // Get belt color classes
  const getBeltColorClasses = (color: string) => {
    return color || 'bg-gray-100 border-gray-300';
  };

  if (loading) {
    return (
      <Layout title={t("common:navigation.beltRequirements")}>
        <div className="flex items-center justify-center py-8">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
          <span className="ml-2 text-muted-foreground">{uiText("Loading belt requirements...")}</span>
        </div>
      </Layout>
    );
  }

  if (beltLevels.length === 0) {
    return (
      <Layout title={t("common:navigation.beltRequirements")}>
        <Card className="border-dashed border-2">
          <CardContent className="flex flex-col items-center justify-center p-12 text-center">
            <Target className="h-12 w-12 text-gray-400 mb-4" />
            <h3 className="text-lg font-semibold text-gray-600 mb-2">
              {uiText("No Belt System Found")}</h3>
            <p className="text-gray-500 mb-6">
              {uiText("Your instructor hasn't set up the belt system yet. Please contact your dojo administrator.")}</p>
          </CardContent>
        </Card>
      </Layout>
    );
  }

  return (
    <Layout title={t("common:navigation.beltRequirements")}>
      <div className="space-y-6">
        <Card className="bg-gradient-to-r from-blue-50 to-indigo-50 border-blue-200">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Target className="h-5 w-5" />
              {t("common:belt.system.title", 'Belt Progression System')}
            </CardTitle>
            <CardDescription>
              {uiText("Dynamic requirements set by your dojo instructor. Track your progress through each belt level.")}</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="flex items-center gap-4">
              <Badge variant="outline" className="text-lg px-3 py-1">
                {beltLevels.length} {' '}{uiText("Belt Levels")}</Badge>
              {profile?.role === 'student' && currentBeltIndex >= 0 && (
                <Badge className="bg-blue-600 text-white">
                  {uiText("Current:")}{' '}{beltLevels[currentBeltIndex]?.name}
                </Badge>
              )}
            </div>
          </CardContent>
        </Card>

        {profile?.role === 'student' && currentBeltIndex >= 0 && (
          <Card className="bg-gradient-to-r from-green-100 to-green-50 border-green-300">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <h3 className="text-lg font-semibold text-green-900">{uiText("Current Progress")}</h3>
                  <p className="text-green-700 font-medium">{beltLevels[currentBeltIndex]?.name}</p>
                  <p className="text-sm text-green-600">
                    {uiText("Next Level:")}{' '}{currentBeltIndex < beltLevels.length - 1 ?
                      beltLevels[currentBeltIndex + 1]?.name : uiText("Advanced Levels")}
                  </p>
                </div>
                <Badge className="bg-green-900 text-white">{uiText("Your Current Rank")}</Badge>
              </div>
            </CardContent>
          </Card>
        )}

        <div className="space-y-6">
          {beltLevels.map((belt, beltIndex) => {
            const isCurrentBelt = beltIndex === currentBeltIndex;
            const isCompleted = profile?.role === 'student' && currentBeltIndex > beltIndex;
            const isNext = profile?.role === 'student' && currentBeltIndex + 1 === beltIndex;
            const groupedRequirements = getRequirementsByCategory(belt.requirements || []);
            const categories = Object.keys(groupedRequirements);

            return (
              <Card key={belt.id} className={`${getBeltColorClasses(belt.color)} ${isCurrentBelt ? 'ring-2 ring-blue-500' : ''}`}>
                <CardHeader>
                  <div className="flex items-center justify-between">
                    <div>
                      <CardTitle className="text-lg">{belt.name}</CardTitle>
                      <div className="flex items-center gap-2 mt-1">
                        <Clock className="h-4 w-4" />
                        <span className="text-sm">{uiText("Level")}{' '}{belt.order_sequence + 1}</span>
                      </div>
                    </div>
                    <div className="flex gap-2">
                      {isCompleted && <Badge className="bg-green-600">{uiText("Completed")}</Badge>}
                      {isCurrentBelt && <Badge className="bg-blue-900">{uiText("Current")}</Badge>}
                      {isNext && <Badge variant="outline" className="border-blue-500 text-blue-700">{uiText("Next Goal")}</Badge>}
                    </div>
                  </div>
                </CardHeader>
                <CardContent>
                  {categories.length === 0 ? (
                    <div className="text-center py-8 text-gray-500">
                      <Target className="h-12 w-12 mx-auto mb-4 text-gray-300" />
                      <p className="text-lg font-medium mb-2">{uiText("No Requirements Set")}</p>
                      <p className="text-sm">
                        {uiText("Your instructor will set specific requirements for this belt level.")}</p>
                    </div>
                  ) : (
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                      {categories.map((category) => (
                        <div key={category} className="space-y-3">
                          <h4 className={`font-medium border-b pb-1 ${belt.color?.includes('text-white') ? 'text-white border-gray-600' : 'text-gray-900'}`}>
                            {category}
                          </h4>
                          <ul className="space-y-2">
                            {groupedRequirements[category].map((requirement, reqIndex) => (
                              <li key={requirement.id || reqIndex} className="flex items-start gap-2 text-sm">
                                {isCompleted ? (
                                  <CheckCircle className="h-4 w-4 text-green-600 mt-0.5 flex-shrink-0" />
                                ) : (
                                  <Circle className="h-4 w-4 text-gray-400 mt-0.5 flex-shrink-0" />
                                )}
                                <div className={`${isCompleted ? 'text-green-700' : belt.color?.includes('text-white') ? 'text-gray-200' : 'text-gray-700'}`}>
                                  <span className="font-medium">{requirement.title}</span>
                                  {requirement.description && (
                                    <p className="text-xs opacity-75 mt-1">{requirement.description}</p>
                                  )}
                                </div>
                              </li>
                            ))}
                          </ul>
                        </div>
                      ))}
                    </div>
                  )}
                </CardContent>
              </Card>
            );
          })}
        </div>

        <Card className="bg-blue-50 border-blue-200">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Users className="h-5 w-5" />
              {uiText("Dojo Information")}</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-sm text-gray-700">
              <p className="mb-2">
                <strong>{uiText("Belt System:")}</strong> {' '}{uiText("This is your dojo's custom belt progression system.")}</p>
              <p className="mb-2">
                <strong>{uiText("Requirements:")}</strong> {' '}{uiText("Each belt level has specific techniques and knowledge requirements set by your instructor.")}</p>
              <p>
                <strong>{uiText("Progress:")}</strong> {' '}{uiText("Work with your instructor to complete requirements and advance through the belt levels.")}</p>
            </div>
          </CardContent>
        </Card>
      </div>
    </Layout>
  );
};

export default BeltRequirements;